import Box, { type BoxProps } from '@mui/material/Box';
import { alpha, type SxProps, type Theme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import type { SystemStyleObject } from '@mui/system';
import type { ReactNode } from 'react';

export type StatusPillTone =
  | 'success'
  | 'info'
  | 'warning'
  | 'error'
  | 'neutral'
  | 'pink'
  | 'orange'
  | 'beige'
  | 'cyan'
  | 'violet'
  | 'indigo'
  | 'magenta'
  | 'lightBlue';

const toneToColors = (tone: StatusPillTone, theme: Theme) => {
  switch (tone) {
    case 'success':
      return { bg: theme.palette.success.light, fg: theme.palette.success.dark };
    case 'info':
      return { bg: theme.palette.primary.light, fg: theme.palette.primary.main };
    case 'warning':
      return { bg: theme.palette.warning.light, fg: theme.palette.warning.dark };
    case 'error':
      return { bg: theme.palette.error.light, fg: theme.palette.error.dark };
    case 'orange':
      return {
        bg: alpha(theme.palette.charts?.tomato ?? theme.palette.warning.main, 0.12),
        fg: theme.palette.charts?.tomato ?? theme.palette.warning.dark,
      };
    case 'cyan':
      return {
        bg: alpha(theme.palette.charts?.lightBlue ?? theme.palette.info.main, 0.12),
        fg: theme.palette.charts?.lightBlue ?? theme.palette.info.main,
      };
    case 'violet':
      return {
        bg: theme.palette.charts?.lavender ?? alpha(theme.palette.primary.main, 0.12),
        fg: theme.palette.charts?.magenta ?? theme.palette.primary.main,
      };
    case 'indigo':
      return {
        bg: alpha(theme.palette.charts?.indigo ?? theme.palette.info.main, 0.12),
        fg: theme.palette.charts?.indigo ?? theme.palette.info.main,
      };
    case 'pink':
      return { bg: theme.palette.error.light, fg: theme.palette.error.dark };
    case 'beige':
      return { bg: theme.palette.warning.light, fg: theme.palette.warning.dark };
    case 'magenta':
      return {
        bg: alpha(theme.palette.charts?.magenta ?? theme.palette.primary.main, 0.12),
        fg: theme.palette.charts?.magenta ?? theme.palette.primary.main,
      };
    case 'lightBlue':
      return {
        bg: alpha(theme.palette.charts?.lightBlue ?? theme.palette.info.main, 0.12),
        fg: theme.palette.charts?.lightBlue ?? theme.palette.info.main,
      };
    default:
      return { bg: theme.palette.action.hover, fg: theme.palette.text.secondary };
  }
};

type StatusPillProps = Omit<BoxProps, 'children'> & {
  label: string;
  tone: StatusPillTone;
  endIcon?: ReactNode;
  'data-test'?: string;
};

export const StatusPill = ({ label, tone, endIcon, sx, 'data-test': dataTest, ...boxProps }: StatusPillProps) => {
  const isSxArray = (
    value: SxProps<Theme>
  ): value is readonly (boolean | SystemStyleObject<Theme> | ((theme: Theme) => SystemStyleObject<Theme>))[] =>
    Array.isArray(value);

  const baseSx: SxProps<Theme> = (theme: Theme) => {
    const { bg, fg } = toneToColors(tone, theme);
    return {
      alignItems: 'center',
      backgroundColor: bg,
      borderRadius: 999,
      color: fg,
      display: 'inline-flex',
      gap: 1,
      height: 24,
      paddingLeft: 1.25,
      paddingRight: endIcon ? 1 : 1.25,
    };
  };

  const mergedSx: SxProps<Theme> = sx
    ? isSxArray(sx)
      ? [baseSx as (theme: Theme) => SystemStyleObject<Theme>, ...sx]
      : [baseSx, sx]
    : baseSx;

  return (
    <Box data-test={dataTest} sx={mergedSx} {...boxProps}>
      <Typography
        component="span"
        sx={{
          color: 'inherit',
          fontSize: 11,
          fontWeight: 'fontWeightBold',
          lineHeight: '16px',
          whiteSpace: 'nowrap',
        }}
      >
        {label}
      </Typography>
      {!!endIcon && (
        <Box
          component="span"
          sx={{
            alignItems: 'center',
            borderRadius: '50%',
            color: 'inherit',
            display: 'inline-flex',
            height: 18,
            justifyContent: 'center',
            width: 18,
          }}
        >
          {endIcon}
        </Box>
      )}
    </Box>
  );
};
